import React, { useCallback, useMemo } from 'react';
import { ClipboardList } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

// Reusable components
import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { DataPoint } from './components/DataPoint';
import { useTranslation } from 'react-i18next';

// Status colors matching the plan
const STATUS_COLORS = {
  submitted: '#3b82f6',   // Blue - Open
  pending: '#6b7280',     // Gray
  onHold: '#ef4444',      // Red
  inProgress: '#60a5fa',  // Light Blue
  resolved: '#10b981'     // Green - Done
};


/**
 * Summary row displaying status counts
 */
const SummaryRow = ({
  submittedCount,
  pendingCount,
  onHoldCount,
  inProgressCount,
  resolvedCount,
  onStatusClick
}) => {
  const { t } = useTranslation();
  return (
  <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
    <DataPoint
      value={submittedCount}
      label={t('shared.open', 'Open')}
      color={STATUS_COLORS.submitted}
      onClick={() => onStatusClick?.('SUBMITTED')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={pendingCount}
      label={t('shared.pending', 'Pending')}
      color={STATUS_COLORS.pending}
      onClick={() => onStatusClick?.('PENDING')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={onHoldCount}
      label={t('shared.on_hold', 'On Hold')}
      color={STATUS_COLORS.onHold}
      onClick={() => onStatusClick?.('ON_HOLD')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={inProgressCount}
      label={t('shared.in_progress', 'In Progress')}
      color={STATUS_COLORS.inProgress}
      onClick={() => onStatusClick?.('IN_PROGRESS')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={resolvedCount}
      label={t('shared.done', 'Done')}
      color={STATUS_COLORS.resolved}
      onClick={() => onStatusClick?.('RESOLVED')}
    />
  </div>
);
}

/**
 * Custom label renderer for pie chart segments
 */
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
  if (value === 0) return null;

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontWeight: 'bold', fontSize: '14px' }}
    >
      {value}
    </text>
  );
};

/**
 * StatusReportWidget - Displays work order distribution by status
 *
 * Shows:
 * - Summary counts for each status (Open, Pending, On Hold, In Progress, Done)
 * - Doughnut chart showing distribution
 */
const StatusReportWidget = ({
  data,
  loading,
  error,
  onStatusClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary } = data || {};
  const {
    submittedCount = 0,
    pendingCount = 0,
    onHoldCount = 0,
    inProgressCount = 0,
    resolvedCount = 0,
    total = 0
  } = summary || {};
  const isEmpty = total === 0;

  // Prepare chart data
  const chartData = useMemo(() => {
    return [
      { name: t('shared.open', 'Open'), value: submittedCount, key: 'submitted', status: 'SUBMITTED' },
      { name: t('shared.pending', 'Pending'), value: pendingCount, key: 'pending', status: 'PENDING' },
      { name: t('shared.on_hold', 'On Hold'), value: onHoldCount, key: 'onHold', status: 'ON_HOLD' },
      { name: t('shared.in_progress', 'In Progress'), value: inProgressCount, key: 'inProgress', status: 'IN_PROGRESS' },
      { name: t('shared.done', 'Done'), value: resolvedCount, key: 'resolved', status: 'RESOLVED' }
    ].filter(item => item.value > 0);
  }, [submittedCount, pendingCount, onHoldCount, inProgressCount, resolvedCount, t]);

  // Handle segment click
  const handleSegmentClick = useCallback((data) => {
    if (onStatusClick && data?.status) {
      onStatusClick(data.status);
    }
  }, [onStatusClick]);

  return (
    <ReportWidget
      title={t('shared.status', 'Status')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={ClipboardList}
      emptyMessage={t('shared.no_data_available', 'No data available for the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        submittedCount={submittedCount}
        pendingCount={pendingCount}
        onHoldCount={onHoldCount}
        inProgressCount={inProgressCount}
        resolvedCount={resolvedCount}
        onStatusClick={onStatusClick}
      />

      {/* Doughnut Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={120}
            label={renderCustomLabel}
            labelLine={false}
            onClick={handleSegmentClick}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={STATUS_COLORS[entry.key]}
                cursor="pointer"
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default StatusReportWidget;
